import axios from 'axios';

const API_URL = "https://werise.mahmoudhaleem.dev";

// Authenticated API instance (for endpoints that require authentication)
const api = axios.create({
  baseURL: API_URL,
});

// Public API instance (for endpoints that don't require authentication)
const publicApi = axios.create({
  baseURL: API_URL,
});

// Add request interceptor for publicApi to handle Content-Type
publicApi.interceptors.request.use(
  (config) => {
    // Set Content-Type for JSON requests, but let axios handle FormData
    if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a request interceptor to the authenticated api instance
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Set Content-Type for JSON requests, but let axios handle FormData
    if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token refresh for the authenticated api instance
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't try to refresh token for auth requests
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/jwt/')) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const response = await axios.post(`${API_URL}/auth/jwt/refresh/`, { refresh: refreshToken });
        const { access } = response.data;
        localStorage.setItem('access_token', access);
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Authentication API (use publicApi for auth endpoints)
export const authAPI = {
  login: (credentials) => publicApi.post('/auth/jwt/create/', credentials),
  refreshToken: (refresh) => publicApi.post('/auth/jwt/refresh/', { refresh }),
  verifyToken: (token) => publicApi.post('/auth/jwt/verify/', { token: typeof token === 'string' ? token : token.token }),
  register: (userData) => publicApi.post('/auth/users/', userData),
  activateUser: (activationData) => publicApi.post('/auth/users/activation/', activationData),
  resendActivation: (email) => publicApi.post('/auth/users/resend_activation/', { email }),
  resetPassword: (email) => publicApi.post('/auth/users/reset_password/', { email }),
  resetPasswordConfirm: (passwordData) => publicApi.post('/auth/users/reset_password_confirm/', passwordData),
  resetEmail: (email) => publicApi.post('/auth/users/reset_email/', { email }),
  resetEmailConfirm: (emailData) => publicApi.post('/auth/users/reset_email_confirm/', emailData),
  setPassword: (passwordData) => publicApi.post('/auth/users/set_password/', passwordData),
  setEmail: (emailData) => publicApi.post('/auth/users/set_email/', emailData),
  getCurrentUser: () => api.get('/auth/users/me/')
};

// src/utils/api.js (add these functions to userAPI)

export const userAPI = {


  getCurrentUser: () => api.get('/auth/users/me/'),
  updateCurrentUser: (userData) => api.put('/auth/users/me/', userData),
  partialUpdateCurrentUser: (userData) => api.patch('/auth/users/me/', userData),
  setPassword: (passwordData) => api.post('/auth/users/set_password/', passwordData),


};

// Submission API (use publicApi for create, authenticated api for others)
export const submissionAPI = {
  createSubmission: (submissionData) => {
    // Axios automatically detects FormData and sets proper headers including boundary
    return publicApi.post('/api/submission/', submissionData);
  },
  getSubmissions: (params = {}) => api.get('/api/submission/', { params }),
  getSubmission: (id) => api.get(`/api/submission/${id}/`),
  getSubmissionsByDevice: (deviceId) => api.get('/api/submission/', { params: { device_id: deviceId } }),
};

// PHC API (use publicApi for get, authenticated api for others)
export const phcAPI = {
  getPHCs: (params = {}) => publicApi.get('/api/phcs/', { params }), // Public endpoint
  createPHC: (phcData) => api.post('/api/phcs/', phcData),
  getPHC: (id) => api.get(`/api/phcs/${id}/`),
  updatePHC: (id, phcData) => api.put(`/api/phcs/${id}/`, phcData),
  partialUpdatePHC: (id, phcData) => api.patch(`/api/phcs/${id}/`, phcData),
  deletePHC: (id) => api.delete(`/api/phcs/${id}/`),
};

// Question API (use publicApi for get, authenticated api for others)
export const questionAPI = {
  getQuestions: (params = {}) => publicApi.get('/api/questions/', { params }), // Public endpoint
  createQuestion: (questionData) => api.post('/api/questions/', questionData),
  getQuestion: (id) => api.get(`/api/questions/${id}/`),
  updateQuestion: (id, questionData) => api.patch(`/api/questions/${id}/`, questionData),
  partialUpdateQuestion: (id, questionData) => api.patch(`/api/questions/${id}/`, questionData),
  deleteQuestion: (id) => api.delete(`/api/questions/${id}/`),
  analyze: (id) => api.post(`/api/questions/${id}/analyze/`),
  getAnalysis: (id) => api.get(`/api/questions/${id}/analysis/`),
};

// Response API (all require authentication)
export const responseAPI = {
  getResponses: (params = {}) => api.get('/api/responses/', { params }),
  createResponse: (responseData) => publicApi.post('/api/responses/', responseData),
  getResponse: (id) => api.get(`/api/responses/${id}/`),
  updateResponse: (id, responseData) => api.put(`/api/responses/${id}/`, responseData),
  partialUpdateResponse: (id, responseData) => api.patch(`/api/responses/${id}/`, responseData),
  deleteResponse: (id) => api.delete(`/api/responses/${id}/`),
};

export default api;